import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Clock, Tag as TagIcon } from 'lucide-react';
import { ContentHeader, FilterSection, ContentGrid, ContentCard, LoadingState, EmptyContentState } from '../../components/shared';
import { articlesService } from '../../services';
import './Articles.css';

export const Articles = () => {
  const [articles, setArticles] = useState([]);
  const [filteredArticles, setFilteredArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilter, setActiveFilter] = useState('All');

  useEffect(() => {
    const fetchArticles = async () => {
      try {
        const articlesData = await articlesService.getPublishedArticles();
        setArticles(articlesData);
        setFilteredArticles(articlesData);
      } catch (error) {
        console.error('Error fetching articles:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchArticles();
  }, []);

  useEffect(() => {
    let filtered = articles;

    if (searchTerm) {
      filtered = filtered.filter(article =>
        article.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        article.excerpt.toLowerCase().includes(searchTerm.toLowerCase()) ||
        article.tags?.some(tag =>
          tag.toLowerCase().includes(searchTerm.toLowerCase())
        )
      );
    }

    if (activeFilter !== 'All') {
      filtered = filtered.filter(article =>
        article.tags?.includes(activeFilter)
      );
    }

    setFilteredArticles(filtered);
  }, [articles, searchTerm, activeFilter]);

  const allTags = [...new Set(articles.flatMap(article => article.tags || []))];
  const filterOptions = ['All', ...allTags];

  const formatDate = (article) => {
    if (article.publishDate) {
      const [year, month, day] = article.publishDate.split('-');
      return new Date(year, month - 1, day).toLocaleDateString('en-US', {
        year: 'numeric', month: 'long', day: 'numeric'
      });
    }
    if (article.createdAt) {
      return article.createdAt.toDate().toLocaleDateString('en-US', {
        year: 'numeric', month: 'long', day: 'numeric'
      });
    }
    return '';
  };

  const estimateReadTime = (content) => {
    if (!content) return '1 min read';
    const wordsPerMinute = 200;
    const wordCount = content.split(' ').length;
    const readTime = Math.ceil(wordCount / wordsPerMinute);
    return `${readTime} min read`;
  };

  return (
    <div className="articles-container">
      <div className="container">
        <ContentHeader
          title="Articles & Insights"
          subtitle="Thoughts, tutorials, and insights about design, user experience, and the creative process"
        />

        <FilterSection
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          searchPlaceholder="Search articles..."
          filterOptions={filterOptions}
          activeFilter={activeFilter}
          onFilterChange={setActiveFilter}
        />

        {loading ? (
          <LoadingState message="Loading articles..." />
        ) : filteredArticles.length === 0 ? (
          <EmptyContentState
            title="No articles found"
            message="Try adjusting your search terms or filters"
          />
        ) : (
          <ContentGrid>
            {filteredArticles.map((article) => (
              <ContentCard
                key={article.id}
                linkTo={`/articles/${article.id}`}
                featuredImage={article.featuredImage}
                title={article.title}
                description={article.excerpt}
                metaItems={[
                  {
                    icon: <Calendar size={14} />,
                    text: formatDate(article)
                  },
                  {
                    icon: <Clock size={14} />,
                    text: estimateReadTime(article.content)
                  }
                ]}
                tags={article.tags || []}
              />
            ))}
          </ContentGrid>
        )}
      </div>
    </div>
  );
};